// Angular imports
import {
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
} from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { Observable, Subject, catchError, forkJoin, takeUntil } from "rxjs";
import { DialogService } from "src/app/_core/services/dialog.service";
import { PerfilViewModel } from "src/app/_core/models/admin/perfil.model";
import { PerfilService } from "src/app/_core/providers/perfil.service";
import {
	AdminViewModel,
	UsuarioActividadProcedureViewModel,
	UsuarioContratoProcedureViewModel,
	UsuarioCentroProcedureViewModel,
	UsuarioZonaProcedureViewModel,
} from "src/app/_core/models/centros/admin.model";
import { EmpleadoViewModel } from "src/app/_core/models/centros/empleado.model";

// Models
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { getHomeMenu } from "src/app/_core/constants/menu.constants";

// Services
import { Policies } from "src/app/_core/models/main/policies.enum";
import { AdminService } from "src/app/_core/providers/admin.service";
import { AuthService } from "src/app/_core/providers/auth.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";
import { MasterService } from "src/app/_core/providers/master.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { BreadcrumbService } from "src/app/_core/services/breadcrumb.service";

@Component({
    selector: "app-admin-page",
    templateUrl: "./admin.page.html",
    styleUrls: ["./admin.page.scss"],
    standalone: false
})
export class AdminPageComponent implements OnInit, OnDestroy {
	// Parameters
	public admin: AdminViewModel;
	public selectedUser: EmpleadoViewModel | null = null;
	public userList: EmpleadoViewModel[] = [];
	public photo: String | ArrayBuffer = "/assets/images/user.png";
	dataSourceUsAc: MatTableDataSource<UsuarioActividadProcedureViewModel>;
	dataSourceUsContrato: MatTableDataSource<UsuarioContratoProcedureViewModel>;
	dataSourceUsZone: MatTableDataSource<UsuarioZonaProcedureViewModel>;
	dataSourceUsCenter: MatTableDataSource<UsuarioCentroProcedureViewModel>;

	// Paginadores. Acotan el render para que no se cuelgue con miles de filas.
	// La tabla "Línea de Negocio" (actividad) NO se pagina: son ~4 negocios.
	@ViewChild("pagContrato") pagContrato: MatPaginator;
	@ViewChild("pagZone") pagZone: MatPaginator;
	@ViewChild("pagCenter") pagCenter: MatPaginator;

	// Perfiles (capacidades) — se asignan también desde aquí, junto a los permisos por elemento.
	perfilesCatalogo: PerfilViewModel[] = [];
	userPerfiles: PerfilViewModel[] = [];
	userPerfilIds: number[] = [];
	private originalPerfilIds: number[] = [];

	// Pestaña activa de las 4 tablas (0=actividad, 1=contrato, 2=zona, 3=centro).
	activeTab = 0;

	// Modo ver/editar + control de cambios sin guardar.
	isEditMode = false;
	isDirty = false;

	// Attributes
	private readonly destroy$ = new Subject<void>();

	// Main
	public contextLocation: ContextLocationViewModel;
	private readonly menu: LinkItem[] = getHomeMenu();

	constructor(
		private readonly _titleService: Title,
		private readonly _translateService: TranslateService,
		private readonly _authService: AuthService,
		private readonly _adminService: AdminService,
		private readonly _masterService: MasterService,
		private readonly _notificationService: NotificationService,
		private readonly _globalService: GlobalService,
		private readonly _breadcrumbService: BreadcrumbService,
		private readonly _cdr: ChangeDetectorRef,
		private readonly _perfilService: PerfilService,
		private readonly _dialogService: DialogService,
	) {
		const miga: MigaDePan[] = [
			{
				nombre: "header.menu.home.admin",
				url: window.location.href,
			},
		];
		this._globalService.limpiarMigas();
		this._globalService.actualizarMigas(miga);
	}

	// Events
	ngOnInit(): void {
		this._globalService.setHeaderOptions(
			[],
			this.contextLocation,
			[],
			this.menu,
		);

		this._titleService.setTitle(
			this._translateService.instant("title.admin.permissions"),
		);

		// Migas: Inicio › Administración › Gestión de permisos (cuelga de /admin).
		this._breadcrumbService.setDetail(
			this._translateService.instant("admin.home.permisos.title"),
		);

		this._loadData();
	}

	/** Guard de navegación: avisa si hay cambios sin guardar. */
	canDeactivate(): Observable<boolean> | boolean {
		if (!this.isDirty) {
			return true;
		}
		return this._dialogService.confirm({
			type: "danger",
			title: this._translateService.instant("admin.unsaved.title"),
			message: this._translateService.instant("admin.unsaved.message"),
			confirmText: this._translateService.instant("common.discard"),
			cancelText: this._translateService.instant("common.keepEditing"),
		});
	}

	ngOnDestroy(): void {
		this._breadcrumbService.clear();
		this.destroy$.next();
		this.destroy$.complete();
	}

	// Private Methods
	private _loadData(): void {
		this._masterService
			.getEmpleados()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.userList = result;
			});

		this._perfilService
			.getPerfiles()
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.perfilesCatalogo = (result ?? []).filter((p) => p.activo);
			});
	}

	private _loadUserPerfiles(usuarioId: number): void {
		this._perfilService
			.getPerfilesByUsuario(usuarioId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.userPerfiles = result ?? [];
				this.originalPerfilIds = this.userPerfiles.map((p) => p.id);
				this.userPerfilIds = [...this.originalPerfilIds];
			});
	}

	private _loadAdmin(adminId: number) {
		this._adminService
			.getAdminById(adminId)
			.pipe(takeUntil(this.destroy$))
			.subscribe((result) => {
				this.PintadoPantalla(result);
			});
	}

	private PintadoPantalla(result: AdminViewModel) {
		this.admin = result;

		// Materializa el bloque *ngIf="admin" para que existan los paginadores ANTES de
		// poblar los dataSources. Así adjuntamos el paginator antes de que la tabla pinte,
		// evitando el render completo (miles de filas) que colgaba la página.
		this._cdr.detectChanges();

		// Línea de Negocio: sin paginar (son ~4 negocios), pero conserva el filtro.
		this.dataSourceUsAc = this._buildPagedDataSource(
			this.admin.usuarioActividadProcedureViewModel,
			(a, b) => (a.actividad ?? "").localeCompare(b.actividad ?? ""),
			null,
		);
		this.dataSourceUsContrato = this._buildPagedDataSource(
			this.admin.usuarioContratoBaseViewModelList,
			(a, b) => (a.contrato ?? "").localeCompare(b.contrato ?? ""),
			this.pagContrato,
		);
		this.dataSourceUsZone = this._buildPagedDataSource(
			this.admin.usuarioZonaProcedureViewModel,
			(a, b) => (a.zona ?? "").localeCompare(b.zona ?? ""),
			this.pagZone,
		);
		this.dataSourceUsCenter = this._buildPagedDataSource(
			this.admin.usuarioCentroViewModelList,
			(a, b) => (a.centro ?? "").localeCompare(b.centro ?? ""),
			this.pagCenter,
		);
	}

	// Crea un MatTableDataSource ordenado con el paginator YA adjunto (render acotado a una página).
	private _buildPagedDataSource<T>(
		rows: T[],
		sortFn: (a: T, b: T) => number,
		paginator: MatPaginator | null,
	): MatTableDataSource<T> {
		const dataSource = new MatTableDataSource<T>((rows ?? []).slice().sort(sortFn));
		if (paginator) {
			dataSource.paginator = paginator;
		}
		return dataSource;
	}

	private saveFile(response: Blob, userId: number): void {
		const blob = new Blob([response], {
			type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		});
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `ResumenUsuario_${userId}.xlsx`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		window.URL.revokeObjectURL(url);
	}

	// Public Methods
	exportToExcel(event: Event, userId: number) {
		event.stopPropagation();
		this._adminService
			.exportResumenToExcel(userId)
			.pipe(
				catchError((error) => {
					console.error("Error exporting to Excel", error);
					throw error;
				}),
			)
			.subscribe((response) => {
				this.saveFile(response, userId);
			});
	}

	applyChanges() {
		this.admin.usuarioContratoViewModelList =
			this.admin.usuarioContratoBaseViewModelList;

		const usuarioId = this.admin.usuarioId;

		// Reconciliación de perfiles: solo las altas/bajas respecto al estado cargado.
		const desired = new Set(this.userPerfilIds ?? []);
		const original = new Set(this.originalPerfilIds ?? []);
		const toAdd = [...desired].filter((id) => !original.has(id));
		const toRemove = [...original].filter((id) => !desired.has(id));

		// Un único guardado controlado: permisos por elemento + perfiles, en paralelo.
		const ops: Observable<unknown>[] = [
			this._adminService.updateAdmin(this.admin),
			...toAdd.map((id) => this._perfilService.asignarPerfil(usuarioId, id)),
			...toRemove.map((id) => this._perfilService.quitarPerfil(usuarioId, id)),
		];

		forkJoin(ops)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (results) => {
					const updated = results[0] as AdminViewModel;
					if (updated) {
						this.PintadoPantalla(updated);
					}
					this._loadUserPerfiles(usuarioId);
					this.isEditMode = false;
					this.isDirty = false;
					this._notificationService.showSuccess(
						this._translateService.instant(
							"messages.savedSuccessfully",
						),
						null,
					);
				},
				error: () => {
					this._notificationService.showError(
						this._translateService.instant(
							"messages.errorsWhileSaving",
						),
					);
				},
			});
	}

	onEditContratoChangeRow(
		row:
			| UsuarioActividadProcedureViewModel
			| UsuarioZonaProcedureViewModel
			| UsuarioContratoProcedureViewModel,
	): void {
		row.edicion_Contrato = !row.edicion_Contrato;

		if (row.edicion_Contrato) {
			row.lectura = true;
			row.edicion_Centro = true;
		} else {
			row.lectura = false;
			row.edicion_Centro = false;
		}
		this.markDirty();
	}

	onEditCenterChangeRow(
		row:
			| UsuarioActividadProcedureViewModel
			| UsuarioZonaProcedureViewModel
			| UsuarioContratoProcedureViewModel
			| UsuarioCentroProcedureViewModel,
	): void {
		row.edicion_Centro = !row.edicion_Centro;

		if (row.edicion_Centro) {
			row.lectura = true;
		} else {
			row.lectura = false;
		}
		this.markDirty();
	}

	onUserSelected(user: EmpleadoViewModel | null): void {
		if (!user) {
			// Limpieza con la X del buscador: se conserva el usuario en gestión.
			return;
		}
		if (this.isDirty) {
			this._dialogService
				.confirm({
					type: "danger",
					title: this._translateService.instant("admin.unsaved.title"),
					message: this._translateService.instant("admin.unsaved.message"),
					confirmText: this._translateService.instant("common.discard"),
					cancelText: this._translateService.instant("common.keepEditing"),
				})
				.pipe(takeUntil(this.destroy$))
				.subscribe((ok) => {
					if (ok) {
						this._selectUser(user);
					}
				});
			return;
		}
		this._selectUser(user);
	}

	private _selectUser(user: EmpleadoViewModel): void {
		this.selectedUser = user;
		this.activeTab = 0;
		this.isEditMode = false;
		this.isDirty = false;
		this._loadAdmin(user.id);
		this._loadUserPerfiles(user.id);
		this.getPhoto(user.email);
	}

	enterEdit(): void {
		this.isEditMode = true;
	}

	cancelEdit(): void {
		this.isEditMode = false;
		this.isDirty = false;
		if (this.selectedUser) {
			this._loadAdmin(this.selectedUser.id);
			this._loadUserPerfiles(this.selectedUser.id);
		}
	}

	markDirty(): void {
		this.isDirty = true;
	}

	// ---- Perfiles (chips toggleables en edición; sustituyen al mat-select multiple) ----

	isPerfilSelected(id: number): boolean {
		return (this.userPerfilIds ?? []).includes(id);
	}

	togglePerfil(id: number): void {
		this.userPerfilIds = this.isPerfilSelected(id)
			? this.userPerfilIds.filter((perfilId) => perfilId !== id)
			: [...this.userPerfilIds, id];
		this.markDirty();
	}

	onRowLecturaChange(row: { lectura?: boolean }): void {
		row.lectura = !row.lectura;
		this.markDirty();
	}

	public getPhoto(email: string): void {
		const unsubscribe$ = new Subject<void>();

		this._authService
			.getUserPhoto(email)
			.pipe(takeUntil(unsubscribe$))
			.subscribe(
				(photoResult) => {
					const reader = new FileReader();
					reader.addEventListener(
						"load",
						() => {
							if (reader && reader.result) {
								this.photo = reader.result;
							} else {
								this.photo = "/assets/images/user.png";
							}
						},
						false,
					);

					if (photoResult) {
						reader.readAsDataURL(photoResult);
					}
				},
				(error) => {
					this.photo = "/assets/images/user.png";
				},
			);

		this.destroy$.pipe(takeUntil(unsubscribe$)).subscribe();
	}

	// Recuento de elementos seleccionados por dimensión (badge en cada pestaña).
	// "Seleccionado" = la fila tiene algún permiso activo (lectura o cualquier edición).
	// Se cuenta sobre el array COMPLETO del usuario, no sobre la página visible.
	get countActividad(): number {
		return this._countSeleccionados(
			this.admin?.usuarioActividadProcedureViewModel,
		);
	}

	get countContrato(): number {
		return this._countSeleccionados(
			this.admin?.usuarioContratoBaseViewModelList,
		);
	}

	get countZona(): number {
		return this._countSeleccionados(this.admin?.usuarioZonaProcedureViewModel);
	}

	get countCentro(): number {
		return this._countSeleccionados(this.admin?.usuarioCentroViewModelList);
	}

	private _countSeleccionados(
		rows?: Array<{
			lectura?: boolean;
			edicion_Contrato?: boolean;
			edicion_Centro?: boolean;
		}>,
	): number {
		return (rows ?? []).filter(
			(r) => r.lectura || r.edicion_Contrato || r.edicion_Centro,
		).length;
	}

	// On input focus: setup filterPredicate to only filter by input column
	setupFilter(column: string) {
		let dataSource: MatTableDataSource<
			| UsuarioCentroProcedureViewModel
			| UsuarioActividadProcedureViewModel
			| UsuarioContratoProcedureViewModel
			| UsuarioZonaProcedureViewModel
		>;

		switch (column) {
			case "actividad":
				dataSource = this.dataSourceUsAc;

				break;
			case "contrato":
				dataSource = this.dataSourceUsContrato;

				break;
			case "zonaSociedad":
				dataSource = this.dataSourceUsZone;

				break;
			case "centro":
				dataSource = this.dataSourceUsCenter;

				break;

			default:
				break;
		}

		dataSource.filterPredicate = (d, filter: string) => {
			const textToSearch = (d[column] && d[column].toLowerCase()) || "";
			return textToSearch.indexOf(filter) !== -1;
		};
	}

	applyFilter(event: KeyboardEvent, tipoDato: string) {
		const filterValue: string = (event.target as HTMLInputElement).value;

		switch (tipoDato) {
			case "actividad":
				this.dataSourceUsAc.filter = filterValue.trim().toLowerCase();

				break;
			case "contrato":
				this.dataSourceUsContrato.filter = filterValue
					.trim()
					.toLowerCase();

				break;
			case "zonaSociedad":
				this.dataSourceUsZone.filter = filterValue.trim().toLowerCase();

				break;
			case "centro":
				this.dataSourceUsCenter.filter = filterValue
					.trim()
					.toLowerCase();

				break;

			default:
				break;
		}
	}
}
